import { range } from 'lodash'
import {
  Heading,
  Link,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useToast,
} from '@chakra-ui/react'
import { PartialWithFieldValue, doc, setDoc } from 'firebase/firestore'

import { collections } from '../../../../AAinitFirebase'
import useActiveUid from '../../../../hooks/useActiveUid'
import useHandleError from '../../../../hooks/useHandleError'
import FirestoreUser from '../../../../types/FirestoreUser'

import TableCell from './TableCell'

// Define a type for the possible course data
type CourseData = {
  startedAt: { month: number; year: number }
  completedAt: { month: number; year: number } | null
  // New field for multiple completions - supports backward compatibility
  completions?: ({ month: number; year: number } | null)[]
}

type Hyperlink = {
  text: string
  link: string
}

const Courses = ({
  editable,
  user,
  courseType,
  courseNames,
  numCourses,
  displayName,
  hyperlink,
}: {
  editable: boolean
  user: FirestoreUser
  courseType: 'bootCamps' | 'essentialsCourses'
  courseNames: string[]
  numCourses: number
  displayName: string
  hyperlink?: Hyperlink | null
}) => {
  const uid = useActiveUid()
  const handleError = useHandleError()
  const toast = useToast()

  const updateUserCourses = async (
    userCourseData: PartialWithFieldValue<FirestoreUser>
  ) => {
    try {
      if (!uid) return
      await setDoc(doc(collections.users, uid), userCourseData, {
        merge: true,
      })
    } catch (e) {
      handleError(e)
    }
  }

  const handleModalSubmit = (
    courseCompletionDate: { month: number; year: number },
    courseIndex: number,
    completionIndex: number // 0 for first time, 1 for second time
  ) => {
    const { month, year } = courseCompletionDate
    if (year < 2023) {
      toast({
        title: 'Invalid Date',
        description: 'Completion date cannot be before January 2023.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
      return
    }

    const courseData = (
      user[courseType] as Partial<Record<number, CourseData | null>>
    )?.[courseIndex]

    // Get existing completions or initialize with backward compatibility
    let completions: ({ month: number; year: number } | null)[] = []

    if (courseData?.completions) {
      completions = [...courseData.completions]
    } else if (courseData?.completedAt) {
      // Backward compatibility: migrate existing completedAt to first completion
      completions = [courseData.completedAt, null]
    } else {
      completions = [null, null]
    }

    // Ensure array has at least 2 slots
    while (completions.length < 2) {
      completions.push(null)
    }

    // Update the specific completion slot
    completions[completionIndex] = courseCompletionDate

    // Update both completions array and maintain backward compatibility
    const updateData: any = {
      [courseType]: {
        [courseIndex]: {
          ...courseData,
          completions,
          // Keep completedAt for backward compatibility (use first completion)
          completedAt: completions[0],
        },
      },
    }

    updateUserCourses(updateData)
  }

  const handleUncheck = (
    courseIndex: number,
    courseData: CourseData,
    completionIndex: number
  ) => {
    // Get existing completions or initialize with backward compatibility
    let completions: ({ month: number; year: number } | null)[] = []

    if (courseData?.completions) {
      completions = [...courseData.completions]
    } else if (courseData?.completedAt) {
      // Backward compatibility: migrate existing completedAt to first completion
      completions = [courseData.completedAt, null]
    } else {
      completions = [null, null]
    }

    // Ensure array has at least 2 slots
    while (completions.length < 2) {
      completions.push(null)
    }

    // Clear the specific completion slot
    completions[completionIndex] = null

    updateUserCourses({
      [courseType]: {
        [courseIndex]: {
          ...courseData,
          completions,
          // Keep completedAt for backward compatibility (use first completion)
          completedAt: completions[0],
        },
      },
    })
  }

  return (
    <Stack>
      <Heading fontSize='md'>7 Day Quick Start</Heading>
      <Text>Which of the Quick Start sessions have you completed?</Text>

      {hyperlink && (
        <Link href={hyperlink.link} isExternal color='purple.600'>
          {hyperlink.text}
        </Link>
      )}
      <TableContainer>
        <Table size='sm' variant='simple'>
          <Thead>
            <Tr>
              <Th width='60%' borderBottom='none'></Th>
              <Th colSpan={2} textAlign='left' borderBottom='none'>
                Reviewed Completely and Action Taken
              </Th>
            </Tr>
            <Tr>
              <Th width='60%'></Th>
              <Th>1st Time</Th>
              <Th>2nd Time</Th>
            </Tr>
          </Thead>
          <Tbody>
            {range(0, numCourses).map((courseIndex) => {
              if (!uid) return null

              const courseData = (
                user[courseType] as Partial<Record<number, CourseData | null>>
              )?.[courseIndex]
              const courseName = courseNames?.[courseIndex]

              // Get completions with backward compatibility
              let completions: ({ month: number; year: number } | null)[] = []

              if (courseData?.completions) {
                completions = [...courseData.completions]
              } else if (courseData?.completedAt) {
                // Backward compatibility: migrate existing completedAt to first completion
                completions = [courseData.completedAt, null]
              } else {
                completions = [null, null]
              }

              // Ensure array has at least 2 slots
              while (completions.length < 2) {
                completions.push(null)
              }

              return (
                <Tr key={courseIndex}>
                  <Td>{courseName}</Td>
                  <TableCell
                    value={completions[0] || null}
                    onUncheck={() => handleUncheck(courseIndex, courseData!, 0)}
                    editable={editable}
                    onCheck={(date) => {
                      handleModalSubmit(date, courseIndex, 0)
                    }}
                    modalTitle={`${displayName} Complete`}
                    modalSubtitle={`When did you complete ${courseNames[courseIndex]}?`}
                    completionIndex={0}
                  />
                  <TableCell
                    value={completions[1] || null}
                    onUncheck={() => handleUncheck(courseIndex, courseData!, 1)}
                    editable={editable && !!completions[0]}
                    onCheck={(date) => {
                      handleModalSubmit(date, courseIndex, 1)
                    }}
                    modalTitle={`${displayName} Complete`}
                    modalSubtitle={`When did you complete ${courseNames[courseIndex]}?`}
                    firstCompletionDate={completions[0]}
                    completionIndex={1}
                  />
                </Tr>
              )
            })}
          </Tbody>
        </Table>
      </TableContainer>
    </Stack>
  )
}

export default Courses
